import classNames from 'classnames';
import {
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { IconButton } from './IconButton';

type AlertType = 'info' | 'low-priority' | 'serious' | 'success' | 'warning';
type AlertSize = 'large' | 'small';

const typeConfig: Record<
  AlertType,
  { bg: string; border: string; buttonText: string; icon: ReactNode }
> = {
  warning: {
    bg: 'bg-amber-1000',
    border: 'border-amber-400',
    buttonText: 'text-amber-400',
    icon: <ExclamationTriangleIcon className="text-amber-400" />,
  },
  serious: {
    bg: 'bg-red-1000',
    border: 'border-red-400',
    buttonText: 'text-red-400',
    icon: <ExclamationCircleIcon className="text-red-400" />,
  },
  success: {
    bg: 'bg-primary-1000',
    border: 'border-primary-400',
    buttonText: 'text-primary-400',
    icon: <CheckCircleIcon className="text-primary-400" />,
  },
  info: {
    bg: 'bg-primary-1000',
    border: 'border-primary-400',
    buttonText: 'text-primary-400',
    icon: <InformationCircleIcon className="text-primary-400" />,
  },
  'low-priority': {
    bg: 'bg-grey-900',
    border: 'border-grey-400',
    buttonText: 'text-white',
    icon: <InformationCircleIcon className="text-grey-400" />,
  },
};

export interface AlertProps {
  message: ReactNode;
  type?: AlertType;
  size?: AlertSize;
  buttonText?: string;
  onButtonClick?: () => void;
  onDismiss?: () => void;
  className?: string;
}

export const Alert = ({
  message,
  type = 'warning',
  size = 'large',
  buttonText,
  onButtonClick,
  onDismiss,
  className,
}: AlertProps) => {
  const config = typeConfig[type];
  const isLarge = size === 'large';

  return (
    <div
      className={classNames(
        'border rounded-xs flex flex-col items-start p-1',
        config.bg,
        config.border,
        className
      )}
    >
      <div className="flex items-start w-full">
        <div
          className={classNames(
            'flex flex-1 items-start gap-3 font-open',
            isLarge ? 'p-3' : 'p-3'
          )}
        >
          <div
            className={classNames('shrink-0', isLarge ? 'size-6' : 'size-5')}
          >
            {config.icon}
          </div>
          <div
            className={classNames(
              'text-white flex-1',
              isLarge ? 'text-base leading-6' : 'text-sm leading-5'
            )}
          >
            {typeof message === 'string' ? (
              <p className="flex-1">{message}</p>
            ) : (
              message
            )}
          </div>
        </div>

        {onDismiss && (
          <IconButton
            onClick={onDismiss}
            Icon={XMarkIcon}
            size="medium"
            type="basic"
          />
        )}
      </div>

      {buttonText && (
        <div className={classNames('pb-1 pl-7 pr-3 w-full')}>
          <button
            type="button"
            onClick={onButtonClick}
            className={classNames(
              'font-bold font-open text-button-normal rounded-xs hover:bg-white-t-900',
              config.buttonText,
              isLarge ? 'px-5 py-3' : 'px-4 py-2'
            )}
          >
            {buttonText}
          </button>
        </div>
      )}
    </div>
  );
};
